/**
 * THE FOLLOWING ARE BROWSER ONLY UTILS THAT CANNOT BE SHARED WITH WEB WORKER
 */
import { SHIPT_ANONYMOUS_ID_KEY, SHIPT_USER_ID_KEY, } from "@shipt/analytics-core";
import { v4 } from "uuid";
import pkg from "../package.json";
import { getSessionInfo } from "./getSessionInfo";
import { isBrowser, } from "@shipt/analytics-core";
import { safeJsonParse } from "./worker";
import { getAnalyticsConfig } from "./config";
export const getCookie = (name) => {
    var match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    if (match)
        return match[2];
};
export const setCookie = (name, value, options = {}) => {
    options = {
        path: "/",
        samesite: "lax",
        secure: false,
        "max-age": 365 * 24 * 60 * 60, // 1 year, in seconds
        ...options,
    };
    let updatedCookie = encodeURIComponent(name) + "=" + encodeURIComponent(value);
    for (let optionKey in options) {
        updatedCookie += "; " + optionKey;
        let optionValue = options[optionKey];
        if (optionValue !== true) {
            updatedCookie += "=" + optionValue;
        }
    }
    document.cookie = updatedCookie;
};
export const deleteCookie = (name) => {
    setCookie(name, "", { "max-age": -1, path: "/" });
};
let memory = {};
let timers = {};
let isMemoryStorageEnabled = true;
/**
 * Disable storing anonymous ID in local memory. This is not recommended
 * to be used in production as users can clear local storage and cookies.
 */
export const disableInMemoryStorage = () => {
    isMemoryStorageEnabled = false;
};
export const setValue = (key, value) => {
    if (!isBrowser())
        return;
    if (isMemoryStorageEnabled) {
        memory[key] = value;
    }
    timers[key] = setTimeout(() => {
        setCookie(key, value);
        // Android WebView doesn't guarantee localstorage is present
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.setItem(key, JSON.stringify(value));
    }, 50);
};
// Sets anonymousId and prefers localStorage before checking cookie
// https://segment.com/docs/connections/spec/best-practices-identify/#anonymousid-generation
// https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/#anonymous-ids
export const getAnonymousId = () => {
    if (!isBrowser())
        return;
    const timer = timers[SHIPT_ANONYMOUS_ID_KEY];
    const memoryAnonymousId = isMemoryStorageEnabled
        ? memory[SHIPT_ANONYMOUS_ID_KEY]
        : undefined;
    if (timer)
        clearTimeout(timer);
    if (memoryAnonymousId) {
        setValue(SHIPT_ANONYMOUS_ID_KEY, memoryAnonymousId);
        return memoryAnonymousId;
    }
    const localStorageAnonymousId = safeJsonParse(localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem(SHIPT_ANONYMOUS_ID_KEY));
    const cookieAnonymousId = getCookie(SHIPT_ANONYMOUS_ID_KEY);
    const anonymousId = cookieAnonymousId || localStorageAnonymousId || v4();
    setValue(SHIPT_ANONYMOUS_ID_KEY, anonymousId);
    return anonymousId;
};
const clearKey = (key) => {
    localStorage === null || localStorage === void 0 ? void 0 : localStorage.removeItem(key);
    delete memory[key];
    deleteCookie(key);
};
export const reset = () => {
    clearKey(SHIPT_USER_ID_KEY);
    clearKey(SHIPT_ANONYMOUS_ID_KEY);
};
export const getUserId = () => {
    if (!isBrowser())
        return;
    return (memory[SHIPT_USER_ID_KEY] ||
        getCookie(SHIPT_USER_ID_KEY) ||
        safeJsonParse(localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem(SHIPT_USER_ID_KEY)) ||
        null);
};
export const getContext = (context) => ({
    ...context,
    library: {
        name: pkg.name,
        version: pkg.version,
    },
    locale: navigator.language,
    page: {
        path: location.pathname,
        referrer: document.referrer,
        search: location.search,
        title: document.title,
        url: location.href,
    },
    sessionId: getSessionInfo().sessionId,
    userAgent: navigator.userAgent,
});
// Setup web worker. created lazily so configureAnalytics() can run first and so
// we never spin up a worker that has no endpoint to post to.
let worker;
let missingConfigLogged = false;
function getWorker() {
    if (worker)
        return worker;
    if (!isBrowser() || !window.Worker)
        return undefined;
    const { apiUrl, writeKey } = getAnalyticsConfig();
    if (!apiUrl || !writeKey) {
        if (!missingConfigLogged) {
            missingConfigLogged = true;
            console.error("[@shipt/analytics-member-web] missing analytics config. Call configureAnalytics({ apiUrl, writeKey }) during app startup, before tracking.");
        }
        return undefined;
    }
    worker = new Worker(`/shipt-analytics-worker.js?v=${pkg.version}`);
    // must be the first message since the worker has no config until it receives this message
    worker.postMessage({ config: { apiUrl, writeKey } });
    return worker;
}
/** @internal test helper */
export function resetWorker() {
    worker = undefined;
    missingConfigLogged = false;
}
export function postWorkerMessage({ options, }) {
    const worker = getWorker();
    if (worker) {
        worker.postMessage({ options });
    }
    else {
        // TODO add error reporting
        console.error("NO WORKER!");
    }
}
export function postWorkerFlushMessage(waitTimeout, debug) {
    return new Promise((resolve) => {
        const worker = getWorker();
        if (worker) {
            worker.postMessage({ flush: true });
            const timeoutToken = setTimeout(() => {
                if (debug) {
                    console.log("In timeout block. Worker has not responded");
                }
                // this tells caller that messages were not flushed within timeout period
                // caller can log this as a warning or do whatever
                resolve(false);
            }, waitTimeout);
            // listen for worker to tell us stuffs flushed
            worker.onmessage = function () {
                if (debug) {
                    console.log("Worker has responded that flush has completed");
                }
                clearTimeout(timeoutToken);
                resolve(true);
            };
            // setup race between hearing back from worker and waitTimeout
        }
        else {
            console.error("NO WORKER!");
            resolve(false);
        }
    });
}
